import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import { MoreVertical, Archive, Trash2, RotateCcw } from "lucide-react";

export default function BusinessActions({ business, onChange, onDeleted, meta }) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState({ future_contact_status: "none" });
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(meta || { dispositions: [], future_contact: [] });

  useEffect(() => {
    if (!meta) api.get("/meta").then((r) => setOpts(r.data)).catch(() => {});
  }, [meta]);

  const archive = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${business.id}/archive`, form);
      toast.success("Business archived");
      setArchiveOpen(false);
      onChange && onChange();
    } catch { toast.error("Could not archive"); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    try {
      await api.post(`/businesses/${business.id}/reactivate`, {});
      toast.success("Business reactivated");
      onChange && onChange();
    } catch { toast.error("Could not reactivate"); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/businesses/${business.id}`);
      toast.success("Record deleted");
      setDeleteOpen(false);
      if (onDeleted) onDeleted();
      else if (onChange) onChange();
    } catch { toast.error("Could not delete"); }
    finally { setBusy(false); }
  };

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <div onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`business-actions-${business.id}`} onClick={stop}
            className="p-1.5 rounded-[4px] text-lr-leather hover:bg-lr-olive/10 hover:text-lr-olive transition-colors">
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-lr-card">
          {business.archived ? (
            <DropdownMenuItem onClick={(e) => { stop(e); reactivate(); }} data-testid="action-reactivate">
              <RotateCcw className="h-4 w-4 mr-2" /> Reactivate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={(e) => { stop(e); setArchiveOpen(true); }} data-testid="action-archive">
              <Archive className="h-4 w-4 mr-2" /> Archive
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={(e) => { stop(e); setDeleteOpen(true); }} data-testid="action-delete"
            className="text-[#8f4a49] focus:text-[#8f4a49]">
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Archive dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent className="bg-lr-card max-h-[85vh] overflow-y-auto lr-scroll" onClick={stop}>
          <DialogHeader>
            <DialogTitle className="font-serif">Archive {business.business_name}</DialogTitle>
            <DialogDescription>Keep this business in Long Range history while removing it from active work.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-lr-charcoal">Disposition (what happened)</Label>
              <Select value={form.disposition || ""} onValueChange={(v) => setForm({ ...form, disposition: v })}>
                <SelectTrigger className="mt-1 bg-white/60" data-testid="archive-disposition"><SelectValue placeholder="Select a disposition" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {(opts.dispositions || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-lr-charcoal">Future contact status</Label>
              <Select value={form.future_contact_status || "none"} onValueChange={(v) => setForm({ ...form, future_contact_status: v })}>
                <SelectTrigger className="mt-1 bg-white/60" data-testid="archive-future-contact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(opts.future_contact || []).map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.future_contact_status === "follow_up_after_date" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-lr-charcoal">Follow up after date</Label>
                  <Input type="date" className="mt-1 bg-white/60" value={form.future_contact_date || ""}
                    onChange={(e) => setForm({ ...form, future_contact_date: e.target.value })} data-testid="archive-followup-date" />
                </div>
                <div>
                  <Label className="text-lr-charcoal">Reason (optional)</Label>
                  <Input className="mt-1 bg-white/60" value={form.future_contact_note || ""}
                    onChange={(e) => setForm({ ...form, future_contact_note: e.target.value })} />
                </div>
              </div>
            )}
            <div>
              <Label className="text-lr-charcoal">Founder note (optional)</Label>
              <Textarea rows={3} className="mt-1 bg-white/60" placeholder="Explain the situation in your own words."
                value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="archive-note" />
              <p className="text-xs text-lr-leather mt-1">Notes are timestamped and added chronologically. New notes never overwrite old ones.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} className="rounded-[4px]">Cancel</Button>
            <Button onClick={archive} disabled={busy} data-testid="archive-confirm" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
              {busy ? "Archiving…" : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-lr-card" onClick={stop}>
          <DialogHeader>
            <DialogTitle className="font-serif">Delete this record?</DialogTitle>
            <DialogDescription>
              You are about to permanently delete <span className="font-medium text-lr-charcoal">{business.business_name}</span>
              {business.owner_name ? <> ({business.owner_name})</> : null} and all of its data. This is for tests, duplicates,
              and mistakes. It will not appear in archived businesses or history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-[4px]">Cancel</Button>
            <Button onClick={del} disabled={busy} data-testid="delete-confirm" className="bg-[#8f4a49] hover:bg-[#7a3f3e] text-lr-cream rounded-[4px]">
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
